import type { GridSelection } from "@glideapps/glide-data-grid";
import {
  type ClipboardEvent as ReactClipboardEvent,
  useCallback,
  useRef,
  useState,
} from "react";
import type { AdaptedSpreadsheetSheet } from "./adapter";
import { parseTabSeparatedClipboard } from "./clipboard-text";
import {
  parseClipboardCellTypes,
  type ClipboardCellTypes,
} from "./paste-utils";
import {
  buildWorkbookClipboardPayload,
  buildWorkbookCopiedRange,
  getGridCopyRange,
  GOOGLE_SHEETS_COMPACT_TABLE_MIME,
  parseWorkbookCopiedRange,
  type WorkbookCopiedRange,
} from "./workbook-clipboard";

interface UseWorkbookClipboardParams {
  activeSheetId: string;
  adaptedSheet: AdaptedSpreadsheetSheet | null;
  gridSelection: GridSelection;
}

interface CapturedPasteMetadata {
  cellTypes: ClipboardCellTypes | null;
  copiedRange: WorkbookCopiedRange | null;
  externalValues: string[][] | null;
}

const emptyCapturedPaste = (): CapturedPasteMetadata => ({
  cellTypes: null,
  copiedRange: null,
  externalValues: null,
});

export const useWorkbookClipboard = ({
  activeSheetId,
  adaptedSheet,
  gridSelection,
}: UseWorkbookClipboardParams) => {
  const capturedPasteRef = useRef<CapturedPasteMetadata>(emptyCapturedPaste());
  const [copiedSelection, setCopiedSelection] = useState<{
    sheetId: string;
    range: NonNullable<GridSelection["current"]>["range"];
  } | null>(null);

  const clearCopiedSelection = useCallback(() => {
    setCopiedSelection(null);
  }, []);

  const handlePasteCapture = useCallback(
    (event: ReactClipboardEvent<HTMLDivElement>) => {
      const html = event.clipboardData?.getData("text/html") ?? "";
      const plainText = event.clipboardData?.getData("text/plain") ?? "";
      const copiedRange = html ? parseWorkbookCopiedRange(html) : null;
      capturedPasteRef.current = {
        cellTypes: html ? parseClipboardCellTypes(html) : null,
        copiedRange,
        // Keep Glide's current values for internal Volca copies. Formula
        // translation verifies those values against the private HTML metadata.
        externalValues:
          !copiedRange && plainText
            ? parseTabSeparatedClipboard(plainText)
            : null,
      };
    },
    [],
  );

  const handleCopyCapture = useCallback(
    (event: ReactClipboardEvent<HTMLDivElement>) => {
      const range = getGridCopyRange(
        gridSelection,
        adaptedSheet?.rowCount ?? 0,
        adaptedSheet?.columns.length ?? 0,
      );
      if (!adaptedSheet || !range) {
        clearCopiedSelection();
        return;
      }

      const copiedRange = buildWorkbookCopiedRange({
        range,
        cells: adaptedSheet.getCellsForSelection(range),
        getCellRecord: adaptedSheet.getCellRecord,
      });
      setCopiedSelection({ sheetId: activeSheetId, range });

      const { googleSheetsCompactTable, textHtml, textPlain } =
        buildWorkbookClipboardPayload(copiedRange);
      event.clipboardData.setData("text/plain", textPlain);
      event.clipboardData.setData("text/html", textHtml);
      event.clipboardData.setData(
        GOOGLE_SHEETS_COMPACT_TABLE_MIME,
        googleSheetsCompactTable,
      );
      event.preventDefault();
      event.stopPropagation();
    },
    [activeSheetId, adaptedSheet, clearCopiedSelection, gridSelection],
  );

  const consumePasteMetadata = useCallback(() => {
    const capturedPaste = capturedPasteRef.current;
    capturedPasteRef.current = emptyCapturedPaste();

    return {
      cellTypes: capturedPaste.cellTypes ?? undefined,
      copiedRange: capturedPaste.copiedRange ?? undefined,
      externalValues: capturedPaste.externalValues ?? undefined,
    };
  }, []);

  return {
    clearCopiedSelection,
    consumePasteMetadata,
    copiedSelection,
    handleCopyCapture,
    handlePasteCapture,
  };
};
